import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import "./SelfApp.css";
import Navbar from "./SelfBybNavbar/HeaderNavbar";
import BybBanner from "./SelfBybBanner/BybBanner";
import BybAudit from "./SelfBybAudit/BybAudit";
import BybAnalysis from "./SelfBybAnalysis/BybAnalysis";
import BybSurveyDeck from "./SelfBybSurveydeck/BybSurveyDeck";
import BybGuides from "./SelfBybGuides/BybGuides";
import BybBrewingProcess from "./SelfBybBrewingprocess/BybBrewingProcess";
import BybStartup from "./SelfBybStartup/BybStartup";
import BybChooseBrand from "./SelfBybChooseyourbrew/BybChooseBrand";
import BybFooter from "./SelfBybFooter/BybFooter";
import BybBonusDecks from "./SelfBybBonusdecks/BybBonusDecks";
import BybWhyBrew from "./SelfBybWhybrew/BybWhyBrew";
import ScrollPage from "./SelfScrollPage";
import Paral from "./SelfParal";
import KitImage from "./SelfKitImage/KitImage";
import BybCustomer from "./SelfBybcustomer/BybCustomer";
import PageSeo from "../Common/Seo/PageSeo";

const SELF_AUDIT_PAGE_URL = "https://brewyourbrand.in/brandselfauditkit";
const SELF_AUDIT_PRODUCT_IMAGE =
  "https://brewyourbrand.blr1.cdn.digitaloceanspaces.com/brandkit.png";

const selfAuditProductSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Brand Self-Audit Kit",
  description:
    "A founder-friendly brand audit kit with a brand clarity checklist, brand strategy tools, and practical resources for building a stronger brand.",
  brand: {
    "@type": "Brand",
    name: "Brew Your Brand",
  },
  category: "Brand strategy toolkit",
  image: SELF_AUDIT_PRODUCT_IMAGE,
  url: SELF_AUDIT_PAGE_URL,
  audience: [
    {
      "@type": "Audience",
      audienceType: "Founders",
    },
    {
      "@type": "Audience",
      audienceType: "Entrepreneurs",
    },
    {
      "@type": "Audience",
      audienceType: "Brand managers",
    },
  ],
  offers: {
    "@type": "Offer",
    url: `${SELF_AUDIT_PAGE_URL}#choose_brew`,
    priceCurrency: "INR",
    price: "7999",
    availability: "https://schema.org/PreOrder",
  },
};

const selfAuditResourceSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Brand Self-Audit Kit tools and resources",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Brand Audit Tool" },
    { "@type": "ListItem", position: 2, name: "Digital Audit Tool" },
    { "@type": "ListItem", position: 3, name: "Brand Strategy Guide" },
    { "@type": "ListItem", position: 4, name: "Visual Identity Guide" },
    { "@type": "ListItem", position: 5, name: "Customer Journey Map" },
    { "@type": "ListItem", position: 6, name: "Competitive Analysis Tool" },
    { "@type": "ListItem", position: 7, name: "Brand Brewing Workbook" },
    { "@type": "ListItem", position: 8, name: "Survey Decks" },
  ],
};

const selfAuditFaqs = [
  {
    question: "What is a brand self-audit?",
    answer:
      "A brand self-audit is a structured review of your brand's clarity, positioning, messaging, visual identity, customer experience, and consistency. It helps founders and teams spot what is working, what feels unclear, and what needs improvement before investing in larger campaigns or redesigns.",
  },
  {
    question: "How do I assess my brand?",
    answer:
      "Start by reviewing your audience, promise, positioning, messaging, visual identity, digital presence, customer journey, and competitor context. A practical brand assessment should compare what your brand says, shows, and delivers across every major customer touchpoint.",
  },
  {
    question: "What is the difference between branding and marketing?",
    answer:
      "Branding defines who you are, what you stand for, how you are positioned, and why customers should trust you. Marketing promotes that brand through campaigns, channels, content, offers, and sales activity. Strong marketing works better when the branding foundation is clear.",
  },
  {
    question: "When should a business run a brand audit?",
    answer:
      "Run a brand audit before a launch, rebrand, website redesign, funding push, new campaign, or growth phase. It is also useful when customer response feels inconsistent, competitors are becoming stronger, or your team is unsure how to explain the brand clearly.",
  },
];

const selfAuditFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${SELF_AUDIT_PAGE_URL}#faq`,
  mainEntity: selfAuditFaqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};
const Index = () => {
  const [isFaqTitleVisible, setIsFaqTitleVisible] = useState(false);
  const faqTitleRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFaqTitleVisible(entry.isIntersecting);
      },
      {
        threshold: 0.5,
      }
    );

    const titleElement = faqTitleRef.current;
    if (titleElement) observer.observe(titleElement);

    return () => {
      if (titleElement) observer.unobserve(titleElement);
    };
  }, []);

  return (
    <>
      <PageSeo
        title="Brand Self-Audit Kit for Brand Audits & Clarity | Brew Your Brand"
        description="Use the Brew Your Brand Self-Audit Kit to run a brand audit, follow a brand clarity checklist, explore strategy tools, and access founder resources."
        canonical="/brandselfauditkit"
        ogTitle="Brand Self-Audit Kit for Brand Audits & Clarity | Brew Your Brand"
        ogDescription="Explore a founder-friendly brand audit kit with a brand clarity checklist, brand strategy tools, and guided resources for sharper positioning and growth."
        ogImage={SELF_AUDIT_PRODUCT_IMAGE}
      />
      <Helmet>
        <meta
          name="keywords"
          content="brand audit, brand clarity checklist, brand strategy tools, founder resources, brand self-audit kit"
        />
        <link
          rel="preconnect"
          href="https://brewyourbrand.blr1.cdn.digitaloceanspaces.com"
          crossOrigin="anonymous"
        />
        <link
          rel="preload"
          as="video"
          href="https://brewyourbrand.blr1.cdn.digitaloceanspaces.com/header_video.webm"
          type="video/webm"
          media="(min-width: 992px)"
        />
        <link
          rel="preload"
          as="image"
          href="https://brewyourbrand.blr1.cdn.digitaloceanspaces.com/box.svg"
          fetchPriority="high"
          media="(max-width: 991.98px)"
        />
        <script type="application/ld+json">
          {JSON.stringify(selfAuditProductSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(selfAuditResourceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(selfAuditFaqSchema)}
        </script>
      </Helmet>
      <Navbar />
      <BybBanner />
      <Paral />
      <section className="self-about-byb-transition" aria-label="About Brew Your Brand">
        <div className="container">
          <p>Brew Your Brand (BYB) is a brand-building platform for founders, entrepreneurs, marketers, and MSME teams that want clearer positioning and stronger customer experience. It offers brand self-audit tools, a quick brand assessment, strategy guides, eMagazine insights, learning resources, and a creative community to help businesses build more consistent, memorable brands.</p>
        </div>
      </section>
      <ScrollPage />
      <BybCustomer />
      <BybAudit />
      <BybAnalysis />
      <BybSurveyDeck />
      <BybGuides />
      <BybBonusDecks />
      <KitImage />
      <BybBrewingProcess />
      <BybStartup />
      <BybWhyBrew />
      <section className="self-audit-faq" id="faq" aria-labelledby="self-audit-faq-title">
        <div className="container">
          <div className="self-audit-faq-header" ref={faqTitleRef}>
            <p className={`fade-in-text ${isFaqTitleVisible ? "visible" : ""}`}>Frequently Asked Questions</p>
            <h2
              id="self-audit-faq-title"
              className={`fade-in-text ${isFaqTitleVisible ? "visible" : ""}`}
            >
              Brand Self-Audit FAQ
            </h2>
          </div>
          <div className="self-audit-faq-grid">
            {selfAuditFaqs.map((faq) => (
              <article className="self-audit-faq-item" key={faq.question}>
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <BybChooseBrand />
      <BybFooter />
    </>
  );
};

export default Index;
